import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { UnassignedTicketsService } from '../../../../core/services/unassigned-tickets.service';
import { UnassignedTicket } from '../../../../core/models/unassigned-ticket.model';

/**
 * Simple organization interface for the dropdown
 */
interface OrganizationOption {
  id: string;
  name: string;
}

/**
 * Assign Ticket Modal Component
 *
 * Modal for assigning an unassigned support ticket to an organization.
 * Displays a searchable organization dropdown and optional profile ID field.
 *
 * @see agent-os/specs/2026-01-23-unassigned-tickets/spec.md
 */
@Component({
  selector: 'app-assign-ticket-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './assign-ticket-modal.component.html',
  styleUrls: ['./assign-ticket-modal.component.scss']
})
export class AssignTicketModalComponent implements OnInit {
  @Input() ticket!: UnassignedTicket;
  @Output() assigned = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  organizations: OrganizationOption[] = [];
  filteredOrganizations: OrganizationOption[] = [];
  selectedOrganizationId = '';
  profileId = '';
  searchQuery = '';
  loading = false;
  loadingOrganizations = true;
  errorMessage = '';

  private readonly apiUrl = environment.apiUrl;

  constructor(
    private unassignedTicketsService: UnassignedTicketsService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadOrganizations();
  }

  /**
   * Load all organizations for the dropdown
   */
  async loadOrganizations(): Promise<void> {
    this.loadingOrganizations = true;

    try {
      const response = await firstValueFrom(
        this.http.get<{
          success: boolean;
          data: { organizations: OrganizationOption[] };
        }>(`${this.apiUrl}/admin/organizations?pageSize=1000`)
      );

      if (response.success && response.data) {
        this.organizations = response.data.organizations;
        this.filteredOrganizations = [...this.organizations];
      }
    } catch (error: any) {
      console.error('Failed to load organizations:', error);
      this.errorMessage = 'Failed to load organizations.';
    } finally {
      this.loadingOrganizations = false;
    }
  }

  /**
   * Filter organizations based on search query
   */
  onSearchChange(): void {
    if (!this.searchQuery.trim()) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }

    const query = this.searchQuery.toLowerCase();
    this.filteredOrganizations = this.organizations.filter(org =>
      org.name.toLowerCase().includes(query)
    );
  }

  /**
   * Handle form submission to assign the ticket
   */
  async onAssign(): Promise<void> {
    if (!this.selectedOrganizationId) {
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    try {
      await this.unassignedTicketsService.assignTicket(
        this.ticket.id,
        this.selectedOrganizationId,
        this.profileId || undefined
      );
      this.assigned.emit();
    } catch (error: any) {
      console.error('Failed to assign ticket:', error);
      this.errorMessage = error.message || 'Failed to assign ticket. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Close the modal
   */
  onCancel(): void {
    this.closed.emit();
  }
}
